import { useCallback, useEffect, useState } from "react";
import { Location } from "history";
import { useModal } from "react-modal-hook";
import { Prompt, useHistory } from "react-router-dom";
import { CommonModal } from "../components/Common/CommonModal";
import Modal from "../components/Common/Modal";
import { Body } from "../components/Common/Typography";

interface UseUnsavedChangesWarningOptions {
  isDirty: boolean;
  title?: string;
  message?: string;
  confirmButtonText?: string;
}

export function useUnsavedChangesWarning({
  isDirty,
  title = "Unsaved changes",
  message = "You have unsaved changes that will be lost if you leave this page. Are you sure you want to leave?",
  confirmButtonText = "Leave page",
}: UseUnsavedChangesWarningOptions) {
  const history = useHistory();
  const [modalVisible, setModalVisible] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<Location | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const handleCancel = useCallback(() => {
    setModalVisible(false);
    setPendingLocation(null);
  }, []);

  const handleConfirm = useCallback(() => {
    setModalVisible(false);
    setConfirmedNavigation(true);
  }, []);

  useEffect(() => {
    if (confirmedNavigation && pendingLocation) {
      history.push({
        pathname: pendingLocation.pathname,
        search: pendingLocation.search,
        state: pendingLocation.state,
      });
      setConfirmedNavigation(false);
      setPendingLocation(null);
    }
  }, [confirmedNavigation, pendingLocation, history]);

  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={handleCancel}>
        <CommonModal
          title={title}
          content={
            <Body size="large" type="regular">
              {message}
            </Body>
          }
          onCancel={handleCancel}
          onSubmit={handleConfirm}
          primaryButtonText={confirmButtonText}
          variant="destructive"
          size="small"
          height="full"
        />
      </Modal>
    ),
    [title, message, confirmButtonText, handleCancel, handleConfirm],
  );

  useEffect(() => {
    if (modalVisible) {
      showModal();
    } else {
      hideModal();
    }
  }, [modalVisible, showModal, hideModal]);

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();

      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  const handleBlockNavigation = useCallback(
    (nextLocation: Location): string | boolean => {
      if (confirmedNavigation) {
        return true;
      }

      setPendingLocation(nextLocation);
      setModalVisible(true);
      return false;
    },
    [confirmedNavigation],
  );

  const UnsavedChangesPrompt = useCallback(
    () => <Prompt when={isDirty} message={handleBlockNavigation} />,
    [isDirty, handleBlockNavigation],
  );

  return { UnsavedChangesPrompt };
}
